import { useSelector } from '@/api/state';
import { getRegion } from '@/utils/stateAccessors';
import { currencyPrice } from '@payaca/helpers/financeHelper';
import { getAssigneeGroupedListedTimelogs } from '@payaca/store/timelogs/timelogsActions';
import {
  AssigneeGroupedListedTimelogs,
  GetAssigneeGroupedListedTimelogsQueryParams,
} from '@payaca/types/listedTimelogTypes';
import { TimelogLinkedEntity } from '@payaca/types/timelogs';
import {
  DurationUnit,
  getReadableStringFromDurationString,
} from '@payaca/utilities/timeUtilities';
import moment from 'moment-timezone';
import { FC, useCallback, useEffect, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import AssigneeTimelogsGrouping from '../assigneeTimelogsGrouping/AssigneeTimelogsGrouping';
import EmptyState from '../emptyState/EmptyState';
import TimelogFilters from '../timelogFilters/TimelogFilters';
import './AssigneeGroupedListedTimelogsControl.sass';

type Props = {
  entity?: Pick<TimelogLinkedEntity, 'entityType' | 'entityId'>;
  disableFilters?: ('periodStart' | 'periodEnd')[];
  triggerRefreshDataAt?: Date;
};

const AssigneeGroupedListedTimelogsControl: FC<Props> = ({
  entity,
  disableFilters,
  triggerRefreshDataAt,
}): JSX.Element | null => {
  const dispatch = useDispatch();

  const [getListedTimelogsRequestData, setGetListedTimelogsRequestData] =
    useState<GetAssigneeGroupedListedTimelogsQueryParams>({
      periodStart: disableFilters?.includes('periodStart')
        ? undefined
        : moment().startOf('month').toDate(),
      periodEnd: disableFilters?.includes('periodEnd')
        ? undefined
        : moment().endOf('month').toDate(),
      entity: entity,
    });

  const [assigneeGroupedListedTimelogs, setAssigneeGroupedListedTimelogs] =
    useState<AssigneeGroupedListedTimelogs | undefined>(undefined);

  const [isGettingTimelogs, setIsGettingTimelogs] = useState(false);

  const region = useSelector(getRegion);

  const handleGetAssigneeGroupedListedTimelogs = useCallback(
    (queryParams: GetAssigneeGroupedListedTimelogsQueryParams) => {
      setIsGettingTimelogs(true);

      dispatch(
        getAssigneeGroupedListedTimelogs.request({
          queryParams: queryParams,
          callback: (timelogs: AssigneeGroupedListedTimelogs) => {
            setAssigneeGroupedListedTimelogs(timelogs);
            setIsGettingTimelogs(false);
          },
          onErrorCallback: () => {
            setIsGettingTimelogs(false);
          },
        })
      );
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'dispatch'
    []
  );

  useEffect(() => {
    handleGetAssigneeGroupedListedTimelogs(getListedTimelogsRequestData);
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'handleGetAssigneeGroupedListedTimelogs'
  }, [getListedTimelogsRequestData]);

  useEffect(() => {
    if (triggerRefreshDataAt && triggerRefreshDataAt <= new Date()) {
      handleGetAssigneeGroupedListedTimelogs(getListedTimelogsRequestData);
    }
  // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'getListedTimelogsRequestData', and 'handleGetAssigneeGroupedListedTimelogs'
  }, [triggerRefreshDataAt]);

  return (
    <div className="assignee-grouped-listed-timelogs-control">
      <div className="filters-container">
        <TimelogFilters
          disableFilters={disableFilters}
          onChange={(value) => {
            setGetListedTimelogsRequestData((d) => ({
              ...d,
              ...value,
            }));
          }}
          appliedFilters={{
            periodStart: getListedTimelogsRequestData.periodStart,
            periodEnd: getListedTimelogsRequestData.periodEnd,
            assignees: getListedTimelogsRequestData.assignees,
            typePublicIds: getListedTimelogsRequestData.typePublicIds,
          }}
        />
      </div>
      {assigneeGroupedListedTimelogs && (
        <>
          <div className="filtered-period-totals">
            <h2>Totals</h2>

            {assigneeGroupedListedTimelogs.totalDuration && (
              <div>
                <span className="label">Time spent:</span>{' '}
                <span className="value">
                  {getReadableStringFromDurationString(
                    assigneeGroupedListedTimelogs.totalDuration,
                    { units: [DurationUnit.HOURS, DurationUnit.MINUTES] }
                  )}
                </span>
              </div>
            )}
            <div>
              <span className="label">Cost:</span>{' '}
              <span className="value">
                {currencyPrice(
                  assigneeGroupedListedTimelogs.totalCalculatedCost,
                  region
                )}
              </span>
            </div>
          </div>
          <div className="assignee-grouped-listed-timelogs-container">
            <ul>
              {assigneeGroupedListedTimelogs.assigneeGroupings.map(
                (assigneeGrouping, i) => {
                  return (
                    <li key={i}>
                      <AssigneeTimelogsGrouping
                        assigneeTimelogsGrouping={assigneeGrouping}
                        onArchiveTimelogSuccess={() =>
                          handleGetAssigneeGroupedListedTimelogs(
                            getListedTimelogsRequestData
                          )
                        }
                        onUpdateTimelogSuccess={() =>
                          handleGetAssigneeGroupedListedTimelogs(
                            getListedTimelogsRequestData
                          )
                        }
                      />
                    </li>
                  );
                }
              )}
            </ul>
          </div>
        </>
      )}
      {!isGettingTimelogs &&
        !assigneeGroupedListedTimelogs?.assigneeGroupings?.length && (
          <EmptyState configName={'timelogs'} arrowIndication={false} />
        )}
    </div>
  );
};

export default AssigneeGroupedListedTimelogsControl;
